import React, { useEffect, useState } from "react";
import { Table, Button, message } from "antd";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { blogimg2 } from "../../components/imagepath";
import HODAssignmentModal from "../../components/Department/HODAssignmentModal";
import { onShowSizeChange, itemRender } from "../../components/Pagination";
import {
  fetchDepartmentById,
  fetchDepartmentFaculties,
  assignDepartmentHOD,
} from "../../services/departmentService";
import { fetchUserById } from "../../services/userService";
import { decrypt, encrypt } from "../../utils/encryption";
import { jwtDecode } from "jwt-decode";
import { BASE_URL_IMAGE } from "../../config/api";

const ViewDepartment = () => {
  const { id } = useParams();
  const decryptedId = decrypt(decodeURIComponent(id));

  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [facultyList, setFacultyList] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedHOD, setSelectedHOD] = useState(null);
  const [hodInfo, setHodInfo] = useState(null);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  useEffect(() => {
    getDepartmentInfo();
    getDepartmentFaculties();
  }, [decryptedId]);

  const getDepartmentInfo = async () => {
    try {
      const data = await fetchDepartmentById(decryptedId);
      setDepartmentInfo({
        name: data.department_name,
        shortCode: data.department_shortcode,
        intake: data.department_intake,
        type: data.department_type,
        status: data.department_status,
        head: data.department_hod, // user_id of HOD
      });
    } catch (error) {
      message.error("Failed to fetch department data");
    }
  };

  const getDepartmentFaculties = async () => {
    try {
      const data = await fetchDepartmentFaculties(decryptedId);
      setFacultyList(data);
    } catch (error) {
      message.error("Failed to fetch department faculties");
    }
  };

  useEffect(() => {
    const fetchHODDetails = async () => {
      if (departmentInfo?.head) {
        try {
          const user = await fetchUserById(departmentInfo.head);
          setSelectedHOD(user.user_id);
          setHodInfo({
            hodid: user.user_id,
            hodemail: user.user_email,
            hodcontact: user.user_contact,
            hodname: user.user_name,
            hodimg: user.user_profile_img
              ? `${BASE_URL_IMAGE}/uploads/userProfiles/${user.user_profile_img}`
              : null,
          });
        } catch (err) {
          console.error("Failed to fetch HOD details");
          setHodInfo(null);
        }
      } else {
        setHodInfo(null);
      }
    };

    fetchHODDetails();
  }, [departmentInfo?.head]);

  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
          localStorage.removeItem("token");
          setLoggedInUserData(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchAndSetUser();
  }, []);

  const handleHODAssignment = () => {
    setIsModalVisible(true);
  };

  const handleSubmitHOD = async (e) => {
    e.preventDefault();
    try {
      if (!selectedHOD || !decryptedId) {
        throw new Error("Please select a faculty member to assign as HOD.");
      }

      await assignDepartmentHOD(decryptedId, selectedHOD, loggedInUserData.user_email, departmentInfo.name);

      setDepartmentInfo((prev) => ({
        ...prev,
        head: selectedHOD,
      }));

      message.success("HOD assigned successfully.");
      setTimeout(() => {
        setIsModalVisible(false);
        setSelectedHOD(null);
      }, 1000);
    } catch (err) {
      message.error(err.message || "Failed to assign HOD.");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: "User Details",
      dataIndex: "user_name",
      render: (text, record) => (
        <div className="d-flex align-items-center gap-3">
          <img
            src={
              record.user_profile_img
                ? `${BASE_URL_IMAGE}/uploads/userProfiles/${record.user_profile_img}`
                : blogimg2
            }
            alt="Profile"
            className="rounded-circle"
            style={{ width: 48, height: 48, objectFit: "cover" }}
          />
          <div>
            <strong>{record.user_name}</strong>
            <div className="text-muted small">{record.user_contact}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "user_email",
    },
    {
      title: "Role",
      dataIndex: "user_role",
      render: (_, record) => {
        let roleLabel = "N/A";
        if (record.user_role === 1) roleLabel = "Admin";
        else if (record.user_role === 2) roleLabel = "Dept. Co-Ordinator";
        else if (record.user_isFaculty === "1") roleLabel = "Faculty";

        return (
          <span className="badge bg-primary text-uppercase">{roleLabel}</span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "user_status",
      render: (status) => (
        <span
          className={`badge ${status == 1 ? "bg-success" : "bg-secondary"}`}
        >
          {status == 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Link
            to={`/user-profile/${encodeURIComponent(encrypt(record.user_id))}`}
            className="btn btn-primary"
          >
            View Profile
          </Link>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "Department" },
    { label: "List Department", path: "/list-department" },
    { label: "View Department" },
  ];

  return (
    <>
      <Helmet>
        <title>View Department | CRM JNEC</title>
      </Helmet>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb items={breadcrumbItems} />

          <div className="card mb-4 shadow-sm border-0 rounded p-4">
            <div className="row">
              <div className="col-md-6 border-end">
                <h4>Department Info</h4>
                <p>
                  <strong>Name:</strong> {departmentInfo?.name || "-"}
                </p>
                <p>
                  <strong>Short Code:</strong>{" "}
                  {departmentInfo?.shortCode || "-"}
                </p>
                <p>
                  <strong>Intake:</strong> {departmentInfo?.intake || "-"}
                </p>
                <p>
                  <strong>Type:</strong> {departmentInfo?.type || "-"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {departmentInfo?.status === "1" ? (
                    <span className="badge bg-success">Active</span>
                  ) : (
                    <span className="badge bg-secondary">Inactive</span>
                  )}
                </p>
              </div>

              <div className="col-md-6">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0">Head of Department</h4>
                  <Button
                    onClick={handleHODAssignment}
                    className="btn btn-primary"
                  >
                    {hodInfo?.hodid ? "Update HOD" : "Assign HOD"}
                  </Button>
                </div>

                {hodInfo?.hodname ? (
                  <div className="d-flex align-items-start gap-3 mt-3">
                    <img
                      src={hodInfo.hodimg || blogimg2}
                      alt="HOD"
                      className="rounded-circle"
                      style={{ width: 70, height: 70, objectFit: "cover" }}
                    />
                    <div>
                      <strong>{hodInfo.hodname}</strong>
                      <div className="text-muted small mb-1">
                        Head of Department
                      </div>
                      <div className="small">
                        📧 {hodInfo.hodemail || "-"}
                        <br />
                        📞 {hodInfo.hodcontact || "-"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mt-3">No HOD assigned yet</p>
                )}
              </div>
            </div>
          </div>

          <div className="card card-table border-0 shadow-sm rounded">
            <div className="card-body">
              <div className="table-responsive">
                <Table
                  pagination={{
                    total: facultyList.length,
                    pageSize: 6,
                    showTotal: (total, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    onShowSizeChange,
                    itemRender,
                  }}
                  columns={columns}
                  dataSource={facultyList}
                  rowKey="user_id"
                />
              </div>
            </div>
          </div>

          <HODAssignmentModal
            isVisible={isModalVisible}
            departmentInfo={departmentInfo}
            facultyData={facultyList.map((f) => ({
              id: f.user_id,
              name: f.user_name,
              role:
                f.user_role == 1
                  ? "Admin"
                  : f.user_role == 2
                  ? "Coordinator"
                  : "Faculty",
            }))}
            selectedHOD={selectedHOD}
            onClose={handleCancel}
            onSubmit={handleSubmitHOD}
            onHODChange={setSelectedHOD}
            error={null}
            success={null}
          />
        </div>
      </div>
    </>
  );
};

export default ViewDepartment;
